import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { ArrowLeft } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authContext";
import { updateGiftCard } from "../../../reducers/giftcard.slice";
import { Button } from "../../../components/ui/button";
import { Select, SelectOption } from "../../../components/ui/select";

const RETAILER_OPTIONS = [
  "AMAZON", "WALMART", "BEST_BUY", "HOME_DEPOT", "KOHL",
  "LOWE", "REI", "GAMESTOP", "NIKE", "ADIDAS",
  "SOUTHWEST", "UBER", "DRAFTKINGS", "FANDUEL",
];

const formatDisplayText = (text) => text?.replace("_", " ");

const inputStyle = {
  width: "100%",
  padding: "10px 14px",
  borderRadius: 10,
  background: "rgba(255,255,255,0.035)",
  border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff",
  fontSize: 14,
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
};

const labelStyle = {
  display: "block",
  color: "#fff",
  marginBottom: 6,
  marginTop: 12,
  fontFamily: "Inter",
  letterSpacing: "0.8px",
  fontSize: 14,
};

const EditGiftCardFormPage = ({ dialogMode = false, cardDetails: cardDetailsProp, onSuccess, onCancel }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const cardDetails = cardDetailsProp || location.state.cardDetails;

  const [showLoader, setShowLoader] = useState(false);

  const [formData, setFormData] = useState({
    retailer: cardDetails?.retailer ?? "",
    gift_card_type: cardDetails?.gift_card_type ?? [],
    amount: cardDetails?.amount ?? "",
    quantity: cardDetails?.quantity ?? "",
    status: cardDetails?.status ?? "ACTIVE",
    boost: cardDetails?.boost ?? "",
    description: cardDetails?.description ?? "",
    tnc: cardDetails?.tnc ?? "",
  });

  const [formTouched, setFormTouched] = useState({
    retailer: false,
    gift_card_type: false,
    amount: false,
    quantity: false,
  });

  const [formErrors, setFormErrors] = useState({
    retailer: "",
    gift_card_type: "",
    amount: "",
    quantity: "",
    boost: "",
  });

  const { showToast } = useContext(AuthContext);

  const handleFormData = (e) => {
    const { name, value } = e.target;

    setFormTouched((prev) => ({ ...prev, [name]: true }));

    if (name === "amount" || name === "boost") {
      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      const parsedVal = parseFloat(value);
      if (isNaN(parsedVal) || parsedVal < 0) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: `${name === "amount" ? "Amount" : "Boost amount"} must be a positive number`,
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    if (name === "quantity") {
      if (value === "") {
        setFormData((prev) => ({ ...prev, [name]: "" }));
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      const parsedVal = parseInt(value, 10);
      if (isNaN(parsedVal) || parsedVal <= 0 || parsedVal !== Number(value)) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: "Quantity must be a positive whole number (minimum 1)",
        }));
        return;
      }
      setFormData((prev) => ({ ...prev, [name]: parsedVal }));
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGiftCardTypeChange = (type, checked) => {
    const newTypes = checked
      ? [...formData.gift_card_type, type]
      : formData.gift_card_type.filter((t) => t !== type);
    setFormData((prev) => ({ ...prev, gift_card_type: newTypes }));
    setFormTouched((prev) => ({ ...prev, gift_card_type: true }));
  };

  const handleUpdateGiftCard = () => {
    setShowLoader(true);
    const payload = { ...formData, id: cardDetails?.id };
    dispatch(updateGiftCard(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      showToast("Gift updated successfully", "success");
      if (dialogMode && onSuccess) onSuccess();
      else navigate(-1);
    });
  };

  const isFormIncomplete =
    !formData.retailer ||
    !formData.gift_card_type?.length ||
    !formData.amount ||
    parseFloat(formData.amount) <= 0 ||
    !formData.quantity ||
    parseInt(formData.quantity) <= 0 ||
    !!formErrors.retailer ||
    !!formErrors.amount ||
    !!formErrors.quantity ||
    !!formErrors.boost ||
    !formData.boost ||
    formData.boost < 0 ||
    formData.boost > 100;

  return (
    <>
      <style>{`
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { display: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>
      <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: dialogMode ? 0 : 32, paddingBottom: dialogMode ? 0 : 48 }}>
        <div style={{
          width: dialogMode ? "100%" : "min(480px, calc(100% - 32px))",
          padding: dialogMode ? 0 : 24,
          backgroundColor: dialogMode ? "transparent" : "#101010",
          borderRadius: dialogMode ? 0 : 10,
          border: dialogMode ? "none" : "1px solid rgba(255,255,255,0.52)",
          marginBottom: dialogMode ? 0 : "3rem",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}>
          {!dialogMode && (
            <>
              <div
                style={{ display: "flex", alignItems: "center", gap: 8, color: "rgba(255,255,255,0.52)", cursor: "pointer", marginBottom: 8 }}
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={18} />
                <span style={{ fontFamily: "inter", fontSize: 14 }}>Back</span>
              </div>

              <div style={{ marginBottom: 8 }}>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: "#fff", margin: "0 0 6px 0" }}>Edit Gift Card</h2>
                <p style={{ color: "rgba(255,255,255,0.52)", fontFamily: "Inter", fontWeight: 300, fontSize: 15, margin: 0 }}>
                  Create a new gift card listing for your inventory.
                </p>
              </div>
            </>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Retailer */}
            <div>
              <label style={labelStyle} htmlFor="retailer">Retailer *</label>
              <Select
                id="retailer"
                name="retailer"
                value={formData.retailer || ""}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, retailer: e.target.value }));
                  setFormTouched((prev) => ({ ...prev, retailer: true }));
                }}
              >
                <SelectOption value="">Select Retailer</SelectOption>
                {RETAILER_OPTIONS.map((r) => (
                  <SelectOption key={r} value={r}>{formatDisplayText(r)}</SelectOption>
                ))}
              </Select>
              {!formData.retailer && formTouched.retailer && (
                <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>Retailer is required</span>
              )}
            </div>

            {/* Gift Card Type */}
            <div>
              <label style={labelStyle}>Gift Card Type *</label>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", padding: "6px 0" }}>
                {["IN_STORE", "ONLINE"].map((type) => (
                  <label key={type} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#fff" }}>
                    <input
                      type="checkbox"
                      checked={formData.gift_card_type.includes(type)}
                      onChange={(e) => handleGiftCardTypeChange(type, e.target.checked)}
                    />
                    {formatDisplayText(type)}
                  </label>
                ))}
              </div>
              {formData.gift_card_type?.length === 0 && formTouched.gift_card_type && (
                <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>At least one type must be selected</span>
              )}
            </div>

            {/* Amount */}
            <div>
              <label style={labelStyle} htmlFor="amount">Amount ($) *</label>
              <input
                id="amount"
                type="number"
                name="amount"
                value={formData.amount}
                placeholder="Enter Gift Card Value"
                onChange={handleFormData}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  const inv = ["e", "E", ",", "+", "-"];
                  if (e.key === "." && e.target.value.includes(".")) { e.preventDefault(); return; }
                  if (inv.includes(e.key) || (!/^\d$/.test(e.key) && e.key !== "." && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter","Home","End"].includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
                }}
                style={{ ...inputStyle, borderColor: formErrors.amount ? "#f87171" : "rgba(255,255,255,0.12)" }}
              />
              {formErrors.amount && <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>{formErrors.amount}</span>}
            </div>

            {/* Quantity */}
            <div>
              <label style={labelStyle} htmlFor="quantity">Quantity *</label>
              <input
                id="quantity"
                type="number"
                name="quantity"
                value={formData.quantity}
                placeholder="Enter Quantity"
                onChange={handleFormData}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  const inv = ["e", "E", ".", ",", "+", "-"];
                  if (inv.includes(e.key) || (!/^\d$/.test(e.key) && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter","Home","End"].includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
                }}
                style={{ ...inputStyle, borderColor: formErrors.quantity ? "#f87171" : "rgba(255,255,255,0.12)" }}
              />
              {formErrors.quantity && <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>{formErrors.quantity}</span>}
            </div>

            {/* Boost Amount */}
            <div>
              <label style={labelStyle} htmlFor="boost">Boost Amount (%) *</label>
              <input
                id="boost"
                type="number"
                name="boost"
                value={formData.boost}
                placeholder="Enter Boost Amount"
                onChange={handleFormData}
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  const inv = ["e", "E", ",", "+", "-"];
                  if (e.key === "." && e.target.value.includes(".")) { e.preventDefault(); return; }
                  if (inv.includes(e.key) || (!/^\d$/.test(e.key) && e.key !== "." && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter","Home","End"].includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
                }}
                style={{ ...inputStyle, borderColor: formErrors.boost ? "#f87171" : "rgba(255,255,255,0.12)" }}
              />
              {formErrors.boost && <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>{formErrors.boost}</span>}
              {formData.boost && (formData.boost < 0 || formData.boost > 100) && (
                <span style={{ display: "block", fontSize: 12, color: "#f87171", marginTop: 4 }}>
                  The Boost Amount should be a positive number between 0 and 100.
                </span>
              )}
            </div>

            {/* Status */}
            <div>
              <label style={labelStyle} htmlFor="status">Status *</label>
              <Select id="status" name="status" value={formData.status || "ACTIVE"} onChange={handleFormData}>
                <SelectOption value="ACTIVE">ACTIVE</SelectOption>
                <SelectOption value="INACTIVE">INACTIVE</SelectOption>
              </Select>
            </div>

            {/* Description */}
            <div>
              <label style={labelStyle} htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                placeholder="Enter Description"
                rows={4}
                onChange={handleFormData}
                style={{ ...inputStyle, resize: "vertical", minHeight: 96 }}
              />
            </div>

            {/* Terms & Conditions */}
            <div>
              <label style={labelStyle} htmlFor="tnc">Terms & Conditions</label>
              <textarea
                id="tnc"
                name="tnc"
                value={formData.tnc}
                placeholder="Enter Terms & Conditions"
                rows={4}
                onChange={handleFormData}
                style={{ ...inputStyle, resize: "vertical", minHeight: 96 }}
              />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 12 }}>
            <Button variant="outline" onClick={() => { if (dialogMode && onCancel) onCancel(); else navigate(-1); }}>Cancel</Button>
            <Button disabled={isFormIncomplete || showLoader} onClick={handleUpdateGiftCard}>Submit</Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditGiftCardFormPage;
